import EventService from '../../../helpers/classes/EventService';
import RenderingService from '../../../helpers/classes/RenderingService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import JourneyManager from '../../classes/JourneyManager';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { ImageProvider } from './image';
import { IMetaData } from '../../interfaces/MetaData';
import { FlowState } from '../../enums/FlowState';
import { ImageService } from '../../../helpers/classes/ImageService';
import { RequiredInputImage } from '../../enums/RequiredInputImage';
import DataService, { DataStoreType } from '../../classes/DataService';
import { IIdScanData } from '../../interfaces/DataFormats';
import { Journey } from '../../classes/Journey';
import {
	removeItems,
} from '../../../helpers/functions/arrays';
import { FileSystemProvider } from './fileSystem';
import { InputProvider } from '../../enums/InputProvider';
import { CameraProvider } from './camera';
import { ScannerProvider } from './scanner';
import { SmartCaptureProvider } from './smart-capture';
import LanguageService from '../../../helpers/classes/LanguageService';
import { TranslationKey } from '../../enums/Translation';
import { HighLevelResult } from '../../enums/HighLevelResult';

export class TripleScanGuidanceProvider {
	public static render(this: JourneyContainer, provider: InputProvider) {
		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.renderToContainer(providerContainer, Templates.TripleScanGuidance))
			.then((providerContainer) => TemplateService.getElements(providerContainer, {
				tripleScanGuidanceContainer: '[data-jcs-element="identity__document__triple__scan__guidance"]',
				tripleScanRetry: '[data-jcs-element="triple__scan__retry__button"]',
				attemptCountContainer: '[data-jcs-element="attempt__count__container"]',
				attemptCounter: '[data-jcs-element="attempt__counter"]',
				failureReasonMessage: '[data-jcs-element="failure__reason__message"]',
				tryAgainMessage: '[data-jcs-element="try__again__message"]',
				blurCheck: '[data-jcs-element="blur-check-reason"]',
				glareCheck: '[data-jcs-element="glare-check-reason"]',
				resolutionCheck: '[data-jcs-element="resolution-check-reason"]',
				edgesCheck: '[data-jcs-element="edges-check-reason"]',
				qualityChecksContainer: '[data-jcs-element="quality-checks-container"]',
				validationFailedReason: '[data-jcs-element="validation-failed-reason"]',
				toggleUploadLabel: '[data-jcs-element="gateway__upload__toggle"]'
			}))
			.then(({
				tripleScanGuidanceContainer,
				tripleScanRetry,
				attemptCountContainer,
				attemptCounter,
				failureReasonMessage,
				tryAgainMessage,
				blurCheck,
				glareCheck,
				resolutionCheck,
				edgesCheck,
				qualityChecksContainer,
				validationFailedReason,
				toggleUploadLabel
			}) => {
				// Focus on the guidance container for accessibility
				if (tripleScanGuidanceContainer instanceof HTMLElement) {
					tripleScanGuidanceContainer.setAttribute('tabindex', '-1'); // Ensure the element is focusable
					tripleScanGuidanceContainer.focus();
				}

				RenderingService.hideObject(blurCheck);
				RenderingService.hideObject(glareCheck);
				RenderingService.hideObject(resolutionCheck);
				RenderingService.hideObject(edgesCheck);
				RenderingService.hideObject(toggleUploadLabel);

				TemplateService.getElements(this._container, {
					uploadToggleButton: '[data-jcs-element="gateway__upload__toggle"]',
				}).then(({ uploadToggleButton }) => {
					RenderingService.hideObject(uploadToggleButton);
				});

				if (this._journey) {
					switch (this._journey.actionAttempt) {
						case 2:
							RenderingService.setText(attemptCounter, '2');
							break;
						case 3:
							RenderingService.setText(attemptCounter, '1');
							break;
						default:
							RenderingService.hideObject(attemptCountContainer);
							break;
					}

					TripleScanGuidanceProvider.setTriplescanReasonMessages(this._journey.tripleScanReason, failureReasonMessage, tryAgainMessage, validationFailedReason);
					TripleScanGuidanceProvider.setQualityChecks(this._journey, blurCheck, glareCheck, resolutionCheck, edgesCheck, qualityChecksContainer);
				}

				if (tripleScanRetry instanceof HTMLElement) {
					tripleScanRetry.addEventListener('click', () => {
						switch (provider) {
							case InputProvider.CAMERA:
								return CameraProvider.render.call(this, true);
							case InputProvider.FILESYSTEM:
								return FileSystemProvider.render.call(this, true);
							case InputProvider.SMART_CAPTURE:
								return SmartCaptureProvider.render.call(this, true);
							default:
								return FileSystemProvider.render.call(this, true);
						}
					});
				}
			});
	}

	private static setTriplescanReasonMessages(tripleScanReason: string, failureReasonMessage: HTMLElement | undefined, tryAgainMessage: HTMLElement | undefined, validationFailedReason: HTMLElement | undefined) {
		switch (tripleScanReason) {
			case HighLevelResult.NotSupported:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_SUPPORTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_SUPPORTED));
				RenderingService.setText(validationFailedReason, LanguageService.getTranslation(TranslationKey.VALIDATION_FAILED_REASON_DOC_UNKNOWN));
				break;
			case HighLevelResult.Refer:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_REFER));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_REFER));
				break;
			case HighLevelResult.NotAccepted:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_ACCEPTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_ACCEPTED));
				RenderingService.setText(validationFailedReason, LanguageService.getTranslation(TranslationKey.VALIDATION_FAILED_REASON_DOC_NOT_ACCEPTED));
				break;
			case HighLevelResult.Expired:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_EXPIRED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_EXPIRED));
				RenderingService.setText(validationFailedReason, LanguageService.getTranslation(TranslationKey.VALIDATION_FAILED_REASON_DOC_EXPIRED));
				break;
			default:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_SUPPORTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_SUPPORTED));
				RenderingService.setText(validationFailedReason, LanguageService.getTranslation(TranslationKey.VALIDATION_FAILED_REASON_DOC_UNKNOWN));
				break;
		}
	}

	private static setQualityChecks(journey: Journey, blurCheck: HTMLElement | undefined, glareCheck: HTMLElement | undefined, resolutionCheck: HTMLElement | undefined, edgesCheck: HTMLElement | undefined, qualityChecksContainer: HTMLElement | undefined) {
		if (journey.blurCheck == "Bad")
			RenderingService.showObject(blurCheck);
		if (journey.glareCheck == "Bad")
			RenderingService.showObject(glareCheck);
		if (journey.lowResolutionCheck == "Bad")
			RenderingService.showObject(resolutionCheck);
		if (journey.fullDocumentInViewCheck == "Bad")
			RenderingService.showObject(edgesCheck);

		if (journey.blurCheck != "Bad" && journey.glareCheck != "Bad" && journey.lowResolutionCheck != "Bad" && journey.fullDocumentInViewCheck != "Bad")
			RenderingService.hideObject(qualityChecksContainer);
	}
}
